import { BaseService } from '../../../../../core/services/base.service';
import { JobFlyerGenerateResponse } from '../models/job-flyer.model';
import {
  JobFlyerLayoutOption,
  mergeFlyerOptions,
} from '../models/job-flyer-options';
import {
  JobFlyerCompositionOption,
  mergeFlyerLayouts,
} from '../models/job-flyer-layouts';

const propagateError = (error: Error): never => {
  throw error;
};

const ignoreNotFound = (error: Error): void => {
  if (!error.message.includes('NOT FOUND')) {
    throw error;
  }
};

const buildFlyerQuery = (
  refresh: boolean,
  themeId?: string,
  layoutId?: string
): string => {
  const params = new URLSearchParams();
  if (refresh) {
    params.set('refresh', 'true');
  }
  if (themeId) {
    params.set('theme', themeId);
  }
  if (layoutId) {
    params.set('layout', layoutId);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

export class JobFlyerService {
  static ListPalettes(
    CallbackCatch?: (error: Error) => void
  ): Promise<JobFlyerLayoutOption[]> {
    return BaseService.Get(
      '/featured/jobs/flyer/options',
      CallbackCatch ?? propagateError,
      false
    ).then((catalog) => mergeFlyerOptions(catalog ?? []));
  }

  static ListLayouts(
    CallbackCatch?: (error: Error) => void
  ): Promise<JobFlyerCompositionOption[]> {
    return BaseService.Get(
      '/featured/jobs/flyer/layouts',
      CallbackCatch ?? propagateError,
      false
    ).then((catalog) => mergeFlyerLayouts(catalog ?? []));
  }

  /** @deprecated Use ListPalettes */
  static ListLayoutOptions(
    CallbackCatch?: (error: Error) => void
  ): Promise<JobFlyerLayoutOption[]> {
    return JobFlyerService.ListPalettes(CallbackCatch);
  }

  /** @deprecated Use ListPalettes */
  static ListThemes(
    CallbackCatch?: (error: Error) => void
  ): Promise<JobFlyerLayoutOption[]> {
    return JobFlyerService.ListPalettes(CallbackCatch);
  }

  static Generate(
    jobId: string,
    options?: {
      refresh?: boolean;
      theme?: string;
      layout?: string;
      option?: string;
    },
    CallbackCatch?: (error: Error) => void
  ): Promise<JobFlyerGenerateResponse> {
    const refresh = options?.refresh ?? false;
    const themeId = options?.theme ?? options?.option;
    const query = buildFlyerQuery(refresh, themeId, options?.layout);
    return BaseService.Post(
      `/featured/jobs/${jobId}/flyer${query}`,
      {},
      CallbackCatch ?? propagateError,
      false
    );
  }

  static GetMetadata(
    jobId: string,
    CallbackCatch?: (error: Error) => void
  ): Promise<JobFlyerGenerateResponse | undefined> {
    return BaseService.Get(
      `/featured/jobs/${jobId}/flyer/metadata`,
      CallbackCatch ?? ignoreNotFound,
      false
    );
  }

  static DownloadPng(
    jobId: string,
    CallbackCatch?: (error: Error) => void
  ): Promise<Blob> {
    return BaseService.Blob(
      'GET',
      `/featured/jobs/${jobId}/flyer`,
      undefined,
      CallbackCatch ?? propagateError,
      false
    );
  }

  static Delete(
    jobId: string,
    CallbackCatch?: (error: Error) => void
  ): Promise<void> {
    return BaseService.Delete(`/featured/jobs/${jobId}/flyer`).catch(
      (error: Error) => {
        if (CallbackCatch) {
          CallbackCatch(error);
        }
        throw error;
      }
    );
  }
}
